import { classifyConnection, type ConnectionState } from "@kitchen/ui";
import { useCallback, useEffect, useRef, useState } from "react";

/** Polls /api/heartbeat every 5 s and derives the connection bar state (architecture §9.7). */
export function useHeartbeat(intervalMs = 5000): {
  state: ConnectionState;
  lastOkAt: number | null;
  now: number;
  retry: () => void;
} {
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [latency, setLatency] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const inFlight = useRef(false);

  const beat = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const started = performance.now();
    try {
      const res = await fetch("/api/heartbeat", { cache: "no-store", signal: AbortSignal.timeout(8000) });
      if (res.ok) {
        setLastOkAt(Date.now());
        setLatency(performance.now() - started);
      }
    } catch {
      // No answer: the silence itself moves the bar to slow, then down.
    } finally {
      inFlight.current = false;
      setNow(Date.now());
    }
  }, []);

  useEffect(() => {
    void beat();
    const id = setInterval(() => void beat(), intervalMs);
    const tick = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
      clearInterval(tick);
    };
  }, [beat, intervalMs]);

  return {
    state: classifyConnection({ lastOkAt, lastLatencyMs: latency, now }),
    lastOkAt,
    now,
    retry: () => void beat(),
  };
}
